/* Native Tap to Pay bridge (Expo shell ↔ web app).
   The shell injects `ttp_capability` into localStorage once the Stripe
   Terminal SDK is available on the device; without it the option is hidden. */

export const isNativeApp = () => {
  try {
    if (localStorage.getItem("ttp_debug_web") === "1") return true; // UI testing on desktop
    return localStorage.getItem("is_native_app") === "true" && !!window.ReactNativeWebView;
  } catch {
    return false;
  }
};

export const getTTPCapability = () => {
  if (!isNativeApp()) return { available: false, reason: "web" };
  try {
    const raw = localStorage.getItem("ttp_capability");
    if (raw) {
      const cap = JSON.parse(raw);
      return { available: !!cap.available, platform: cap.platform, reason: cap.reason };
    }
  } catch { /* fall through */ }
  // Native shell without the Tap to Pay build — needs an app update.
  return { available: false, reason: "app_update_required" };
};

let _seq = 0;

/** Ask the native shell to run the Stripe Terminal Tap to Pay flow.
 *  Resolves with {ok, status, canceled, error}. Never rejects. */
export const collectTapToPay = (params, { onStatus, timeoutMs = 180000 } = {}) =>
  new Promise((resolve) => {
    const requestId = `ttp_${Date.now()}_${++_seq}`;
    let done = false;
    const finish = (detail) => {
      if (done) return;
      done = true;
      window.removeEventListener("fieldz-ttp", handler);
      clearTimeout(timer);
      resolve(detail);
    };
    const handler = (e) => {
      const d = e.detail || {};
      if (d.request_id !== requestId) return;
      if (d.type === "status") {
        onStatus?.(d.message, d.phase, d.progress);
        return;
      }
      finish(d);
    };
    const timer = setTimeout(
      () => finish({ ok: false, error: "No response from the app — please update the Fieldz AI app and try again." }),
      timeoutMs
    );
    window.addEventListener("fieldz-ttp", handler);
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "TTP_COLLECT",
        payload: { request_id: requestId, ...params },
      }));
    } catch {
      finish({ ok: false, error: "Tap to Pay is only available in the Fieldz AI mobile app." });
    }
  });

export const cancelTapToPay = () => {
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "TTP_CANCEL" }));
  } catch { /* noop */ }
};

/** Open a URL in the device's real browser (needed for Apple's web onboarding
 *  / Apple ID sign-in). Uses the native bridge inside the app, window.open on
 *  the desktop web. */
export const openExternalUrl = (url) => {
  try {
    if (window.FieldzNative?.openExternal) { window.FieldzNative.openExternal(url); return; }
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "OPEN_EXTERNAL", payload: { url } }));
  } catch {
    window.open(url, "_blank", "noopener");
  }
};

/** Apple req 4.1 (iOS 18+): ask the native shell to present Apple's
 *  ProximityReaderDiscovery education sheet. Resolves {ok, error?}. */
export const requestNativeTTPEducation = (timeoutMs = 20000) =>
  new Promise((resolve) => {
    const requestId = `edu_${Date.now()}_${++_seq}`;
    let done = false;
    const finish = (detail) => {
      if (done) return;
      done = true;
      window.removeEventListener("fieldz-ttp", handler);
      clearTimeout(timer);
      resolve(detail);
    };
    const handler = (e) => {
      const d = e.detail || {};
      if (d.request_id !== requestId) return;
      finish(d);
    };
    const timer = setTimeout(() => finish({ ok: false, error: "timeout" }), timeoutMs);
    window.addEventListener("fieldz-ttp", handler);
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "TTP_EDUCATION",
        payload: { request_id: requestId },
      }));
    } catch {
      finish({ ok: false, error: "unavailable" });
    }
  });

/** Apple req 1.6: ask the device to retrieve the merchant's Tap to Pay T&C
 *  acceptance status FROM APPLE via the Stripe SDK. Resolves {ok, linked?,
 *  error?}. Old app builds never respond → resolves {ok:false} on timeout. */
export const getAppleLinkStatus = (params, timeoutMs = 25000) =>
  new Promise((resolve) => {
    const requestId = `link_${Date.now()}_${++_seq}`;
    let done = false;
    const finish = (detail) => {
      if (done) return;
      done = true;
      window.removeEventListener("fieldz-ttp", handler);
      clearTimeout(timer);
      resolve(detail);
    };
    const handler = (e) => {
      const d = e.detail || {};
      if (d.request_id !== requestId) return;
      if (d.type === "status") return;
      finish(d);
    };
    const timer = setTimeout(() => finish({ ok: false, error: "timeout" }), timeoutMs);
    window.addEventListener("fieldz-ttp", handler);
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "TTP_LINK_STATUS",
        payload: { request_id: requestId, ...params },
      }));
    } catch {
      finish({ ok: false, error: "unavailable" });
    }
  });

/** Warm up / configure the reader (Apple req 1.5 & setup flow). Resolves with
 *  {ok, ready?, error?}. Safe to call fire-and-forget. Never rejects. */
export const prepareTapToPay = (params, { onStatus, timeoutMs = 120000 } = {}) =>
  new Promise((resolve) => {
    const requestId = `prep_${Date.now()}_${++_seq}`;
    let done = false;
    const finish = (detail) => {
      if (done) return;
      done = true;
      window.removeEventListener("fieldz-ttp", handler);
      clearTimeout(timer);
      resolve(detail);
    };
    const handler = (e) => {
      const d = e.detail || {};
      if (d.request_id !== requestId) return;
      if (d.type === "status") {
        onStatus?.(d.message, d.phase, d.progress);
        return;
      }
      finish(d);
    };
    const timer = setTimeout(() => finish({ ok: false, error: "timeout" }), timeoutMs);
    window.addEventListener("fieldz-ttp", handler);
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "TTP_PREPARE",
        payload: { request_id: requestId, ...params },
      }));
    } catch {
      finish({ ok: false, error: "Tap to Pay is only available in the Fieldz AI mobile app." });
    }
  });
